export type Policy<TGlobalCtx, TCtx = Record<string, unknown>> = (
  globalContext: TGlobalCtx,
  context: TCtx
) => boolean;

export type DefinePoliciesReturnType<
  TGlobalCtx,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TPolicies extends Record<string, (ctx: any) => boolean>
> = {
  bindContext: (globalCtx: TGlobalCtx) => TPolicies;
} & TPolicies;

/**
 * Helper function to define resource policies (create, update, show, etc.)
 *
 * @template TGlobalCtx - Type of the global context that will be available to all policies
 * @template TPolicies - Type of the policies object containing resource-specific policies
 *
 * @param init - Function that initializes policies with global context
 * @returns An object containing:
 *   - All defined policies (create, update, show, etc.)
 *   - bindContext method to bind global context to policies
 *
 * @example
 * ```typescript
 * const userPolicies = definePolicies((globalCtx) => ({
 *   create: (ctx) => globalCtx.isAdmin || ctx.userId === globalCtx.currentUserId,
 *   update: (ctx) => globalCtx.isAdmin || ctx.userId === globalCtx.currentUserId,
 *   show: (ctx) => true, // Everyone can view
 * }));
 *
 * // Use with global context
 * const boundPolicies = userPolicies.bindContext({ isAdmin: true, currentUserId: '123' });
 * const canCreate = boundPolicies.create({ userId: '456' });
 * ```
 */
export const definePolicies = <
  TGlobalCtx,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TPolicies extends Record<string, (ctx: any) => boolean>
>(
  init: (globalCtx: TGlobalCtx) => TPolicies,
): DefinePoliciesReturnType<TGlobalCtx, TPolicies> => {
  const bindContext = (globalCtx: TGlobalCtx): TPolicies => init(globalCtx);

  return {
    ...init({} as TGlobalCtx),
    bindContext,
  };
};
